import { Plugin, NuxtConfig } from '@nuxt/types'
import VueI18n from 'vue-i18n'
import { Store } from 'vuex'
import { AxiosStatic } from 'axios'
import { UserCarrier } from '~/carrier/user/UserCarrier'
import { AuthApiFactory } from '~/plugins/api/auth-api-factory'
import { AuthJsonApiFactory } from '~/plugins/api/auth-json-api-factory'
import { AppApiFactory } from '~/plugins/api/app-api-factory'
import { IApiFactoryConstructor } from '~/plugins/api/api-factory/IApiFactory'
import { OrganizationCarrier } from '~/carrier/organizations/OrganizationCarrier'
import { IRootState } from '~/store'
import { ProductsCarrier } from '~/carrier/products/ProductsCarrier'
import { DlcApiFactory } from '~/plugins/api/dlc-api-factory'
import { DocumentsCarrier } from '~/carrier/documents/DocumentsCarrier'
import { EtkCarrier } from '~/carrier/etk/EtkCarrier'
import { I18nCarrier } from '~/carrier/i18n/I18nCarrier'
import { NewsCarrier } from '~/carrier/news/NewsCarrier'
import { PrioServiceCarrier } from '~/carrier/prio-service/PrioServiceCarrier'
import { ServicePartnerCarrier } from '~/carrier/service-partner/ServicePartnerCarrier'
import { NotificationsCarrier } from '~/carrier/notifications/NotificationsCarrier'

export interface IApi {
  readonly documents: DocumentsCarrier
  readonly etk: EtkCarrier
  readonly i18n: I18nCarrier
  readonly news: NewsCarrier
  readonly organization: OrganizationCarrier
  readonly prioService: PrioServiceCarrier
  readonly products: ProductsCarrier
  readonly user: UserCarrier
  readonly servicePartner: ServicePartnerCarrier
  readonly notifications: NotificationsCarrier
}

export class ApiPlugin {
  private static readonly NUXT_PLUGIN_KEY = 'api'

  constructor(
    private readonly axios: AxiosStatic,
    private readonly config: NuxtConfig,
    private readonly store: Store<IRootState>,
    private readonly i18n: VueI18n
  ) {}

  static install: Plugin = ({ $axios, store, app }, inject) => {
    const apiPlugin = new ApiPlugin(
      $axios,
      app.$envConfig,
      store,
      app.i18n as VueI18n
    )
    inject(ApiPlugin.NUXT_PLUGIN_KEY, apiPlugin.setup())
  }

  private setup(): IApi {
    const authApi = this.createApi(AuthApiFactory)
    const authJsonApi = this.createApi(AuthJsonApiFactory)
    const appApi = this.createApi(AppApiFactory)
    const dlcApi = this.createApi(DlcApiFactory)

    return {
      documents: new DocumentsCarrier(dlcApi),
      etk: new EtkCarrier(appApi),
      i18n: new I18nCarrier(appApi),
      news: new NewsCarrier(appApi, this.i18n),
      organization: new OrganizationCarrier(appApi),
      prioService: new PrioServiceCarrier(appApi),
      products: new ProductsCarrier(appApi),
      user: new UserCarrier(authApi, authJsonApi),
      servicePartner: new ServicePartnerCarrier(appApi),
      notifications: new NotificationsCarrier(appApi)
    }
  }

  private createApi(ApiFactory: IApiFactoryConstructor) {
    const apiFactory = new ApiFactory(this.config, this.store, this.i18n)
    return apiFactory.create(this.axios)
  }
}

export default ApiPlugin.install
